import React, { useState } from "react";
import { Plus, AlertCircle } from "lucide-react";

export const SettingsTab = ({
    connectionName,
    setConnectionName,
    syncSchedule,
    setSyncSchedule,
    customCron,
    setCustomCron,
    saveSettings,
    saving,
    deleteConnection
}) => {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    return (
        <div className="p-6 space-y-6">
            {/* Connection Settings */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-900 mb-6">Settings</h3>

                <div className="space-y-6">
                    <div className="grid grid-cols-3 gap-4 items-start">
                        <div>
                            <p className="font-medium text-gray-900">Connection name</p>
                            <p className="text-sm text-gray-500">Name for your connection</p>
                        </div>
                        <div className="col-span-2">
                            <input
                                type="text"
                                value={connectionName}
                                onChange={(e) => setConnectionName(e.target.value)}
                                className="w-full max-w-md border border-gray-300 rounded-lg px-4 py-2"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 items-start">
                        <div>
                            <p className="font-medium text-gray-900">Tags</p>
                        </div>
                        <div className="col-span-2">
                            <button className="p-2 border border-gray-300 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 items-start">
                        <div>
                            <p className="font-medium text-gray-900">Schedule type</p>
                            <p className="text-sm text-gray-500">
                                How you want your syncs to be triggered
                            </p>
                        </div>
                        <div className="col-span-2">
                            <select
                                value={syncSchedule}
                                onChange={(e) => setSyncSchedule(e.target.value)}
                                className="w-full max-w-md border border-gray-300 rounded-lg px-4 py-2"
                            >
                                <option value="manual">Manual</option>
                                <option value="hourly">Every hour</option>
                                <option value="daily">Every 24 hours</option>
                                <option value="custom">Cron</option>
                            </select>
                        </div>
                    </div>

                    {syncSchedule === "custom" && (
                        <div className="grid grid-cols-3 gap-4 items-start">
                            <div>
                                <p className="font-medium text-gray-900">Cron expression</p>
                                <p className="text-sm text-gray-500">Schedule a precise time for your sync using Quartz syntax</p>
                            </div>
                            <div className="col-span-2">
                                <div className="flex max-w-md gap-4">
                                    <input
                                        type="text"
                                        value={customCron}
                                        onChange={(e) => setCustomCron(e.target.value)}
                                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2 font-mono text-sm"
                                        placeholder="0 0 12 * * ?"
                                    />
                                    <select className="border border-gray-300 rounded-lg px-4 py-2 bg-gray-50 text-gray-500" disabled>
                                        <option>UTC</option>
                                    </select>
                                </div>
                                <p className="mt-2 text-xs text-gray-500">
                                    <a href="https://www.quartz-scheduler.org/documentation/quartz-2.3.0/tutorials/crontrigger.html" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:align-baseline">
                                        Quartz syntax reference
                                    </a>
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="pt-4">
                        <button
                            onClick={saveSettings}
                            disabled={saving}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 mr-3"
                        >
                            {saving ? "Saving..." : "Save changes"}
                        </button>
                        <button className="px-4 py-2 text-gray-600 hover:text-gray-900">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-white rounded-lg shadow p-6 border-2 border-red-200">
                <div className="space-y-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="font-medium text-gray-900">Refresh your data</p>
                            <p className="text-sm text-gray-500">
                                Refreshing your data will replace all data in your destination
                                by reloading all data from the source.
                            </p>
                        </div>
                        <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                            Refresh your data
                        </button>
                    </div>

                    <div className="flex items-start justify-between">
                        <div>
                            <p className="font-medium text-gray-900">Clear your data</p>
                            <p className="text-sm text-gray-500">
                                Clearing your data will delete all data in your destination.
                            </p>
                        </div>
                        <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                            Clear your data
                        </button>
                    </div>

                    <div className="flex items-start justify-between">
                        <div>
                            <p className="font-medium text-gray-900">Delete connection</p>
                            <p className="text-sm text-gray-500">
                                No data will be deleted from your source and destination. This
                                cannot be undone without a full re-sync.
                            </p>
                        </div>
                        <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                            Delete this connection
                        </button>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                        <div className="flex items-center mb-4">
                            <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
                            <h3 className="text-lg font-semibold text-gray-900">
                                Delete Connection?
                            </h3>
                        </div>

                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete this connection? This action
                            cannot be undone.
                        </p>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-900"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={deleteConnection}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
